import React, { useEffect, useMemo, useState } from "react";
import { generateProductUrl } from '../../../../utils/productUrlUtils';
import { productListApiService } from "../../../../service/admin/productApiService";
import { adminApiService } from "../../../../service/admin/adminApiService";
import { useLoader } from "../../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../../base/context/MessageProvider";
import { MdDelete, MdModeEdit, MdDescription } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { GrRevert } from "react-icons/gr";
import { IoMdEye, IoMdEyeOff } from "react-icons/io";
import { FaSearch, FaSync, FaPlus, FaUpload, FaDownload, FaCheckCircle } from "react-icons/fa";
import DeletePopUp from "../../../../component/library/deletePopUp";
import ExcelTable from "../../../../component/library/excelTable";
import { exportToCSV } from "react-easy-export";
import ExcelJS from "exceljs";
import usePermissions from "../../../../hooks/usePermissions";
import { getAuthLocalStorage } from "../../../../storage/localStorage";
// import Logo from "../../../../website/assets/images/common/logo_no_image.png";
import "./style.css";

export default function ProductList() {
  const [productData, setProductData] = useState([]);
  const [products, setProducts] = useState({ name: "", sku: "", seller: "", status: "" });
  const [limit, setLimit] = useState(20);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState({ currentPage: 1, totalPages: 1, totalItems: 0 });
  const [open, setOpen] = useState(false);
  const [id, setId] = useState("");
  const [categories, setCategories] = useState([{ name: "All Categories", value: "" }]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [exportProcessing, setExportProcessing] = useState(false);
  const [exportStatus, setExportStatus] = useState("");
  const [exportProgress, setExportProgress] = useState(0);
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();
  const { canEdit, canDelete } = usePermissions();
  const [uploadFile, setUploadFile] = useState();

  // Get current user ID for per-user table settings
  const authData = getAuthLocalStorage();
  const currentUserId = authData?.loginHeader || null;

  const fetchSuppliers = () => {
    adminApiService.get("/users?role=supplier&limit=500")
      .then((res) => {
        const supplierList = res?.data?.users || [];
        setSuppliers([
          { _id: "", name: "All Suppliers" },
          ...supplierList.map(s => ({ _id: s._id, name: `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.email }))
        ]);
      })
      .catch(() => setSuppliers([{ _id: "", name: "All Suppliers" }]));
  };

  const fetchCategories = () => {
    adminApiService.get("/category")
      .then((res) => {
        const categoryList = res?.data || [];
        setCategories([
          { name: "All Categories", value: "" },
          ...categoryList.map(c => ({ name: c.c_name, value: c.c_name }))
        ]);
      })
      .catch(() => setCategories([{ name: "All Categories", value: "" }]));
  };

  useEffect(() => { fetchSuppliers(); fetchCategories(); }, []);

  const handleFile = (e) => setUploadFile(e.target.files[0]);
  const handleOnChange = (e) => setProducts({ ...products, [e.target.name]: e.target.value });

  const numericValue = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (!value) return 0;
    const parsed = parseFloat(String(value).replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const formatPrice = (value, currencySymbol = "₹") => {
    const amount = numericValue(value);
    return `${currencySymbol}${amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return "-"; }
  };

  const truncateText = (text = "", maxLength = 50) => {
    if (typeof text !== "string") return "";
    return text.length > maxLength ? `${text.substring(0, maxLength)}…` : text;
  };

  // Helper function to convert column index (0-based) to Excel column letter (A, B, C, ..., Z, AA, AB, ...)
  const getExcelColumnLetter = (colIndex) => {
    let result = '';
    let num = colIndex;
    while (num >= 0) {
      result = String.fromCharCode(65 + (num % 26)) + result;
      num = Math.floor(num / 26) - 1;
    }
    return result;
  };

  const handleExportCsv = async () => {
    setExportProcessing(true);
    setExportProgress(0);
    setExportStatus("Preparing export...");
    try {
      const source = selectedProducts.length ? selectedProducts : tableRows;
      
      if (source.length === 0) {
        message.showToast("No data to export", ToastTypes.WARNING);
        setExportProcessing(false);
        return;
      }
      
      // Use existing data from table (no need to fetch again)
      setExportProgress(10);
      setExportStatus(`Preparing ${source.length} product(s) for export...`);
      const fullProductData = source.map((entry) => {
        // Use raw data that's already available in the table
        return entry.raw || entry;
      });

      // First pass: Find maximum number of attributes in any single variant
      let maxAttributeCount = 0;
      fullProductData.forEach((product) => {
        const variations = product.variations || [];
        variations.forEach((variant) => {
          if (variant && Array.isArray(variant.skuAttributes)) {
            const attributeCount = variant.skuAttributes.filter(attr => {
              const attrName = attr.attributeNameTrans || attr.attributeName || "";
              const attrValue = attr.valueTrans || attr.value || "";
              return attrName && attrValue;
            }).length;
            if (attributeCount > maxAttributeCount) {
              maxAttributeCount = attributeCount;
            }
          }
        });
      });

      // Find maximum imgUrl array length across all products
      let maxImgUrlLength = 0;
      fullProductData.forEach((product) => {
        const imgUrlLength = Array.isArray(product.imgUrl) ? product.imgUrl.length : 0;
        if (imgUrlLength > maxImgUrlLength) {
          maxImgUrlLength = imgUrlLength;
        }
      });

      // Create export data with one row per variant
      const exportData = [];
      
      fullProductData.forEach((product) => {
        const variations = product.variations || [];
        // Normalize image URL
        const mainImage = product.imgUrl?.[0] ? normalizeUrl(product.imgUrl[0]) : "";
        const productSku = product.sku || product.parentSku || "";

        // Helper function to extract attributes from skuAttributes as ordered array
        const extractAttributesArray = (variant) => {
          const attributes = [];
          if (variant && Array.isArray(variant.skuAttributes)) {
            variant.skuAttributes.forEach((attr) => {
              const attrName = attr.attributeNameTrans || attr.attributeName || "";
              const attrValue = attr.valueTrans || attr.value || "";
              if (attrName && attrValue) {
                attributes.push(`${attrName} - ${attrValue}`);
              }
            });
          }
          return attributes;
        };

        // Helper function to create export row
        const createExportRow = (variant = null) => {
          // Use variant image if available, otherwise use product image
          const variantImage = variant?.imgUrl?.[0] || variant?.image || "";
          const displayImage = variantImage ? normalizeUrl(variantImage) : mainImage;
          
          // Extract attributes from skuAttributes as ordered array
          const variantAttributesArray = variant ? extractAttributesArray(variant) : [];
          
          // Extract image description from variant skuAttributes
          const imageDescription = variant && Array.isArray(variant.skuAttributes) 
            ? variant.skuAttributes.find(attr => attr.skuImageUrl)?.skuImageUrl || ""
            : "";
          
          const row = {
            "Image": "", // Blank column for formula
            "Image URL": displayImage,
            "Product Name": product.title || "",
            "Product SKU": (() => {
              // Keep as string to prevent scientific notation in Excel
              if (productSku) {
                return String(productSku);
              }
              return "";
            })(),
            "Variant SKU": variant ? (() => {
              // Keep as string to prevent scientific notation in Excel
              const sku = variant.sku || variant.skuId || variant.specId || "";
              if (sku) {
                return String(sku);
              }
              return "";
            })() : (() => {
              // Keep as string to prevent scientific notation in Excel
              const sku = product.sku || "";
              if (sku) {
                return String(sku);
              }
              return "";
            })(),
            // Dynamic attribute columns - based on maximum count, fill in order
            ...(Array.from({ length: maxAttributeCount }, (_, index) => {
              const attrValue = variantAttributesArray[index] || "";
              return { [`Attribute ${index + 1}`]: attrValue };
            }).reduce((acc, obj) => ({ ...acc, ...obj }), {})),
            "Category": "",
            "Quantity": variant ? (variant.amountOnSale || 0) : (product.quantity || 0),
            "Weight": variant ? (variant.weight || 0) : (product.weight || 0),
            "Length": variant ? (variant.length || 0) : (product.length || ""),
            "Width": variant ? (variant.width || 0) : (product.width || ""),
            "Height": variant ? (variant.height || 0) : (product.height || ""),
            "AP": variant ? (variant.ap || variant.actualPrice || "") : "",
            "CP": variant ? (variant.consignPrice || variant.cp || "") : "",
            "DS Final Price": variant ? formatPrice(variant.finalPrice || 0) : formatPrice(product.sp || 0),
            "WS Final Price": variant ? formatPrice(variant.wholesaleFinalPrice || 0) : "",
            "Variant Image": "", // Blank column for formula
            "Variant Image URL": variant && Array.isArray(variant.skuAttributes) 
              ? (variant.skuAttributes.find(attr => attr.skuImageUrl)?.skuImageUrl || "") 
              : "",
            "Offer ID": product.offerId || "",
            "GST": product.gst || "",
            "HSN": product.hsn || "",
            "Handling Days": product.handling_days || "",
            "Max Order Quantity": product.max_order_quantity || product.moq || "",
            "Local Shipping": variant ? (variant.localShipping || variant.ls || 0) : (product.local_shipping || 0),
            "Generic Keywords": product.generic_keywords || "",
            "Search Keywords": Array.isArray(product.keywordSearch) ? product.keywordSearch.join(", ") : (product.keywordSearch || ""),
            "Bullet Point 1": Array.isArray(product.bullet_points) ? (product.bullet_points[0] || "") : "",
            "Bullet Point 2": Array.isArray(product.bullet_points) ? (product.bullet_points[1] || "") : "",
            "Bullet Point 3": Array.isArray(product.bullet_points) ? (product.bullet_points[2] || "") : "",
            "Bullet Point 4": Array.isArray(product.bullet_points) ? (product.bullet_points[3] || "") : "",
            "Bullet Point 5": Array.isArray(product.bullet_points) ? (product.bullet_points[4] || "") : "",
            // Dynamic image columns based on imgUrl array length - blank column + URL column
            ...(Array.from({ length: maxImgUrlLength }, (_, index) => {
              const imgUrl = Array.isArray(product.imgUrl) && product.imgUrl[index] 
                ? normalizeUrl(product.imgUrl[index]) 
                : "";
              return { 
                [`Img ${index + 1}`]: "", // Blank column for formula
                [`Img ${index + 1} URL`]: imgUrl
              };
            }).reduce((acc, obj) => ({ ...acc, ...obj }), {})),
            "Description": product.description || "",
            "Image Description": product.img_description || "",
          };
          return row;
        };

        // If product has no variants, create one row with product data
        if (variations.length === 0) {
          exportData.push(createExportRow());
        } else {
          // Create one row for each variant
          variations.forEach((variant) => {
            exportData.push(createExportRow(variant));
          });
        }
      });

      // Export to Excel with column width based on content
      if (exportData.length === 0) {
        message.showToast("No data to export", ToastTypes.WARNING);
        setExportProcessing(false);
        return;
      }

      setExportProgress(30);
      setExportStatus(`Preparing Excel file with ${exportData.length} rows...`);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('product_list_export');
      
      // Get all column keys from first row
      const columnKeys = Object.keys(exportData[0]);
      
      // Define columns with headers
      const columns = columnKeys.map((key) => ({
        header: key,
        key: key,
        width: 15, // Default width, will be adjusted
      }));

      worksheet.columns = columns;

      // Style header row
      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF4472C4' }
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.height = 20;

      setExportProgress(40);
      setExportStatus(`Adding data rows...`);
      // Add data rows
      for (let rowIndex = 0; rowIndex < exportData.length; rowIndex++) {
        // Update progress (40% to 70%)
        const dataProgress = 40 + Math.floor((rowIndex / exportData.length) * 30);
        setExportProgress(dataProgress);
        const rowData = exportData[rowIndex];
        const row = worksheet.addRow(rowData);
        const excelRowIndex = rowIndex + 2; // +2 because row 1 is header
        
        // Set row height
        row.height = 35;
        
        // Process each cell
        row.eachCell((cell, colNumber) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          cell.alignment = { vertical: 'middle', horizontal: 'left' };
          
          // Format SKU columns as text to prevent scientific notation
          const columnKey = columnKeys[colNumber - 1];
          if (columnKey === 'Product SKU' || columnKey === 'Variant SKU') {
            cell.numFmt = '@'; // '@' is the text format code in Excel
            // Ensure value is treated as text
            if (cell.value !== null && cell.value !== undefined) {
              cell.value = String(cell.value);
            }
          }
        });
      }
      
      // Auto-fit columns based on content length
      let descriptionColumnWidth = null;
      
      // First pass: calculate all column widths and find Description column width
      columnKeys.forEach((key, index) => {
        const column = worksheet.getColumn(index + 1);
        
        // Set width for Image columns (blank columns for formulas)
        if (key === 'Image' || key === 'Variant Image' || (key.startsWith('Img ') && !key.endsWith(' URL'))) {
          column.width = 15; // Fixed width for blank image columns
          return;
        }
        
        // Set width for Image URL columns (containing URLs)
        if (key === 'Image URL' || key === 'Variant Image URL' || (key.startsWith('Img ') && key.endsWith(' URL'))) {
          let maxUrlLength = key.length; // Start with header length
          worksheet.getColumn(key).eachCell({ includeEmpty: false }, (cell) => {
            const cellValue = cell.value ? String(cell.value) : '';
            if (cellValue.length > maxUrlLength) {
              maxUrlLength = cellValue.length;
            }
          });
          const urlWidth = Math.min(50, Math.max(15, maxUrlLength + 5));
          column.width = urlWidth;
          return;
        }
        
        let maxLength = key.length; // Start with header length
        
        // Find max content length in this column
        worksheet.getColumn(key).eachCell({ includeEmpty: false }, (cell) => {
          const cellValue = cell.value ? String(cell.value) : '';
          if (cellValue.length > maxLength) {
            maxLength = cellValue.length;
          }
        });
        
        // Set width with padding (min 10, max 60 for better readability)
        const calculatedWidth = Math.min(60, Math.max(10, maxLength + 3));
        column.width = calculatedWidth;
        
        // Store Description column width
        if (key === 'Description') {
          descriptionColumnWidth = calculatedWidth;
        }
      });
      
      // Second pass: Set Image Description column width to match Description column width
      if (descriptionColumnWidth !== null) {
        columnKeys.forEach((key, index) => {
          if (key === 'Image Description') {
            const column = worksheet.getColumn(index + 1);
            column.width = descriptionColumnWidth;
          }
        });
      }

      // Generate buffer and download
      setExportProgress(95);
      setExportStatus("Generating Excel file...");
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Product_List_Export.xlsx';
      link.style.display = 'none';
      
      // Append to body, trigger download, then remove
      document.body.appendChild(link);
      
      setExportProgress(100);
      setExportStatus("Downloading file...");
      
      // Use setTimeout to ensure the link is properly attached
      setTimeout(() => {
        link.click();
        setExportProgress(100);
        setExportStatus("Export completed!");
        
        // Clean up after a short delay
        setTimeout(() => {
          // Check if link still exists in DOM before removing
          if (link && link.parentNode) {
            try {
              document.body.removeChild(link);
            } catch (error) {
              console.warn("Link already removed:", error);
            }
          }
          // Always revoke URL to free memory
          if (url) {
            try {
              window.URL.revokeObjectURL(url);
            } catch (error) {
              console.warn("Error revoking URL:", error);
            }
          }
          message.showToast(`Exported ${exportData.length} rows (${fullProductData.length} products)`, ToastTypes.SUCCESS);
          setExportProcessing(false);
        }, 500);
      }, 100);
    } catch (error) {
      console.error("Export error:", error);
      message.showToast("Error exporting data: " + (error.message || "Unknown error"), ToastTypes.ERROR);
      setExportProcessing(false);
    }
  };

  const handleVerifySeller = () => {
    if (selectedProducts.length === 0) {
      message.showToast("Please select products to verify seller", ToastTypes.ERROR);
      return;
    }
    const eligibleProducts = selectedProducts.map((entry) => entry.raw || entry).filter((item) => item.status === "5");
    if (eligibleProducts.length === 0) {
      message.showToast("No products with status 'completed' found in selection", ToastTypes.ERROR);
      return;
    }
    const productIds = eligibleProducts.map(item => item._id);
    loader.showInlineLoader("Verifying seller status...", "medium");
    productListApiService.bulkUpdateVerifiedSeller(productIds)
      .then((res) => {
        loader.hideLoader();
        if (res.status.code === 200) {
          message.showToast(`Successfully verified ${res.data.modifiedCount} products`, ToastTypes.SUCCESS);
          fetchProductsList();
          setSelectedProducts([]);
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        loader.hideLoader();
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
      });
  };

  const queryParams = `?name=${encodeURIComponent(products.name) || ""}&sku=${products.sku || ""}&category=${encodeURIComponent(categoryFilter) || ""}&supplier=${supplierFilter || ""}&status=${statusFilter || ""}&page=${page}&limit=${limit}`;

  const fetchProductsList = () => {
    setLoading(true);
    productListApiService.getAllProductsList(queryParams)
      .then((res) => {
        setLoading(false);
        setInitialLoading(false);
        if (res.status.code === 200) {
          setProductData(res.data.products);
          setPages({ currentPage: res.data.currentPage, totalPages: res.data.totalPages, totalItems: res.data.totalProduct });
          setPage(res.data.currentPage);
        } else if (res.status.code === 402) {
          navigate("/login");
        } else {
          message.showToast(res.error.message, ToastTypes.ERROR);
        }
      })
      .catch((err) => {
        message.showToast(err?.message || "Server error", ToastTypes.ERROR);
        setLoading(false);
        setInitialLoading(false);
      });
  };

  useEffect(() => { fetchProductsList(); }, [page, limit]);

  const searchProducts = () => {
    setPage(1);
    fetchProductsList();
  };

  const handleEdit = (id) => navigate(`/admin/products/edit/${id}`);
  const handleDescription = (id) => navigate(`/admin/products/details?id=${id}`);
  // const handleProduct = (product) => navigate(`/product-details?ali=1&offer=${product.sku}`);
    const handleProduct = (product) => {
      const url = generateProductUrl({
        id: product._id,
        offer: product.offerId,
        isAli: !!product.offerId,
        title: product.title,
        variantId: product.variantId || undefined,
      });
      window.open(url, '_blank', 'noopener,noreferrer');
    };

  const handleStatus = (id, status) => {
    productListApiService.editProducts(JSON.stringify({ status }), id)
      .then(() => fetchProductsList())
      .catch((err) => console.log(err));
  };

  const handleDelete = (id) => { setId(id); setOpen(true); };
  const handleClose = () => setOpen(false);

  const handleSubmit = () => {
    loader.showLoading(true);
    productListApiService.deleteProducts(id)
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) { fetchProductsList(); setOpen(false); }
        else message.showToast(res.error.message, ToastTypes.ERROR);
      })
      .catch((err) => { message.showToast(err?.message || "Server error", ToastTypes.ERROR); loader.showLoading(false); });
  };

  const handleRevert = (id, deleted) => {
    productListApiService.editProducts(JSON.stringify({ is_delete: deleted }), id)
      .then(() => fetchProductsList())
      .catch((err) => console.log(err));
  };

  const handleUploadProduct = () => {
    if (!uploadFile) { message.showToast("Please choose a file to upload", ToastTypes.ERROR); return; }
    loader.showLoading(true);
    const data = new FormData();
    data.append("file", uploadFile);
    productListApiService.addExcelProduct(data)
      .then((res) => {
        loader.showLoading(false);
        if (res.status.code === 200) { message.showToast("Upload successful", ToastTypes.SUCCESS); fetchProductsList(); }
        else message.showToast(res.error.message, ToastTypes.ERROR);
      })
      .catch((err) => { message.showToast(err?.message || "Server error", ToastTypes.ERROR); loader.showLoading(false); });
  };

  const normalizeUrl = (url) => {
    if (!url) return "";
    try { return String(url).replace(/^http:\/\//, "https://"); } catch { return url; }
  };

  const getVariantMixPrices = (item) => {
    const vars = item?.variations || [];
    if (!vars.length) {
      const vp = numericValue(item.vendor_price);
      return { minAp: vp, maxAp: vp, minCp: null, maxCp: null };
    }
    let minAp = null, maxAp = null, minCp = null, maxCp = null;
    for (const v of vars) {
      const ap = typeof v.ap === "number" && Number.isFinite(v.ap) ? v.ap : null;
      const cp = typeof v.cp === "number" && Number.isFinite(v.cp) ? v.cp : (typeof v.consignPrice === "number" && Number.isFinite(v.consignPrice) ? v.consignPrice : null);
      if (ap != null) {
        if (minAp == null || ap < minAp) minAp = ap;
        if (maxAp == null || ap > maxAp) maxAp = ap;
      }
      if (cp != null) {
        if (minCp == null || cp < minCp) minCp = cp;
        if (maxCp == null || cp > maxCp) maxCp = cp;
      }
    }
    if (minAp == null) minAp = numericValue(item.vendor_price);
    if (maxAp == null) maxAp = minAp;
    return { minAp, maxAp, minCp, maxCp };
  };

  const tableRows = useMemo(() => {
    return (productData || []).map((item, index) => {
      const { minAp, maxAp, minCp, maxCp } = getVariantMixPrices(item);
      return {
        _id: item._id,
        serialNumber: (page - 1) * limit + index + 1,
        raw: item,
        image: normalizeUrl(item?.thumbnailImage || item?.imgUrl?.[0]),
        title: item.title || "-",
        sku: item.sku || "-",
        brand: item.brand || "Generic",
        minAp,
        maxAp,
        minCp,
        maxCp,
        sp: numericValue(item.sp),
        status: item.status,
        verified_seller: item.verified_seller,
        subscribers: item.userSubscribe?.length || 0,
        createdAt: item.createdAt,
        location: item.location || "-",
        categoryName: item.c_name || "-",
      };
    });
  }, [productData, page, limit]);

  const columns = useMemo(() => [
    {
      key: "serialNumber",
      label: "#",
      width: 35,
      render: (value) => <span className="pl-serial">{value}</span>,
    },
    {
      key: "image",
      label: "IMAGE",
      width: 65,
      render: (value) => (
        <div className="pl-image-cell">
          {value ? (
            <img src={value} alt="" referrerPolicy="no-referrer" />
          ) : (
            <div className="pl-no-image">-</div>
          )}
        </div>
      ),
    },
    {
      key: "title",
      label: "Product",
      width: 300,
      render: (value, row) => (
        <div className="pl-product-cell" onClick={() => handleProduct(row.raw)}>
          <span className="pl-product-title">{value}</span>
          <span className="pl-product-category">{row.categoryName}</span>
        </div>
      ),
    },
    {
      key: "sku",
      label: "SKU",
      width: 140,
      render: (value, row) => (
        <div className="pl-sku-cell" onClick={() => handleProduct(row.raw)}>
          <span className="pl-sku-text">{value}</span>
        </div>
      ),
    },
    // {
    //   key: "brand",
    //   label: "Brand",
    //   width: 100,
    //   render: (value) => <span className="pl-brand">{value || "Generic"}</span>,
    // },
    {
      key: "vendor_price",
      label: "Vendor Price",
      width: 240,
      render: (_, row) => {
        const fmt = (v) => v != null ? `¥${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;
        const dropshipVal = row.minAp != null
          ? (row.minAp === row.maxAp ? fmt(row.minAp) : `(${fmt(row.minAp)} ~ ${fmt(row.maxAp)})`)
          : "–";
        const cpMin = row.minCp ?? row.maxCp;
        const cpMax = row.maxCp ?? row.minCp;
        const wholesaleVal = cpMin != null || cpMax != null
          ? (cpMin === cpMax ? fmt(cpMin) : `(${fmt(cpMin) ?? "–"} ~ ${fmt(cpMax) ?? "–"})`)
          : "–";
        const priceRangeList = row.raw?.productSaleInfo?.priceRangeList || [];
        const useRangeList = Array.isArray(priceRangeList) && priceRangeList.length > 2;
        return (
          <div className="pl-vendor-price-cell">
            <div className="pl-vendor-block pl-vendor-dropship">
              <span className="pl-vendor-label">Dropship</span>
              <span className="pl-vendor-value">{dropshipVal}</span>
            </div>
            <div className="pl-vendor-block pl-vendor-wholesale">
              <span className="pl-vendor-label">Wholesale</span>
              {useRangeList ? (
                <div className="pl-wholesale-stacked">
                  <div className="pl-wholesale-prices-row">
                    ({priceRangeList.map((r, i) => (
                      <span key={i} className="pl-wholesale-price-item">
                        {fmt(r.price) ?? "–"}{i < priceRangeList.length - 1 ? " ~ " : ""}
                      </span>
                    ))})
                  </div>
                  <div className="pl-wholesale-qty-row">
                    ({priceRangeList.map((r, i) => {
                      const start = r.startQuantity ?? "–";
                      const nextStart = priceRangeList[i + 1]?.startQuantity;
                      const qtyLabel = nextStart ? `${start}-${nextStart - 1}` : `${start}+`;
                      return (
                        <span key={i} className="pl-wholesale-qty-item">
                          ({qtyLabel}){i < priceRangeList.length - 1 ? " - " : ""}
                        </span>
                      );
                    })})
                  </div>
                </div>
              ) : (
                <span className="pl-vendor-value">{wholesaleVal}</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: "sp",
      label: "Selling Price",
      width: 260,
      render: (value, row) => {
        const item = row.raw || {};
        const fmt = (v) => v != null && Number.isFinite(Number(v)) ? formatPrice(Number(v)) : null;
        const vars = item?.variations || [];
        const dropshipPrices = vars.map((v) => v.finalPrice != null && Number.isFinite(Number(v.finalPriceMRP)) ? Number(v.finalPriceMRP) : null).filter((n) => n != null);
        const wholesalePrices = vars.map((v) => v.wholesaleFinalPrice != null && Number.isFinite(Number(v.wholesaleFinalPrice)) ? Number(v.wholesaleFinalPrice) : null).filter((n) => n != null);
        const minDropship = dropshipPrices.length ? Math.min(...dropshipPrices) : (value != null ? numericValue(value) : null);
        const maxDropship = dropshipPrices.length ? Math.max(...dropshipPrices) : (value != null ? numericValue(value) : null);
        const minWholesale = wholesalePrices.length ? Math.min(...wholesalePrices) : (value != null ? numericValue(value) : null);
        const maxWholesale = wholesalePrices.length ? Math.max(...wholesalePrices) : (value != null ? numericValue(value) : null);
        const dropshipVal = minDropship != null
          ? (minDropship === maxDropship ? fmt(minDropship) : `(${fmt(minDropship)} ~ ${fmt(maxDropship)})`)
          : "–";
        const priceRanges = Array.isArray(item.priceRanges) ? item.priceRanges : [];
        const usePriceRangesForWholesale = priceRanges.length >= 2;
        const wholesaleVal = !usePriceRangesForWholesale && minWholesale != null
          ? (minWholesale === maxWholesale ? fmt(minWholesale) : `(${fmt(minWholesale) ?? "–"} ~ ${fmt(maxWholesale) ?? "–"})`)
          : null;
        return (
          <div className="pl-vendor-price-cell">
            <div className="pl-vendor-block pl-vendor-dropship">
              <span className="pl-vendor-label">Dropship</span>
              <span className="pl-vendor-value">{dropshipVal}</span>
            </div>
            <div className="pl-vendor-block pl-vendor-wholesale">
              <span className="pl-vendor-label">Wholesale</span>
              {usePriceRangesForWholesale ? (
                <div className="pl-wholesale-stacked">
                  <div className="pl-wholesale-prices-row">
                    ({priceRanges.map((r, i) => (
                      <span key={i} className="pl-wholesale-price-item">
                        {fmt(r.finalPrice) ?? "–"}{i < priceRanges.length - 1 ? " ~ " : ""}
                      </span>
                    ))})
                  </div>
                  <div className="pl-wholesale-qty-row">
                    ({priceRanges.map((r, i) => {
                      const start = r.startQuantity ?? "–";
                      const nextStart = priceRanges[i + 1]?.startQuantity;
                      const qtyLabel = nextStart != null ? `${start}-${nextStart - 1}` : `${start}+`;
                      return (
                        <span key={i} className="pl-wholesale-qty-item">
                          ({qtyLabel}){i < priceRanges.length - 1 ? " - " : ""}
                        </span>
                      );
                    })})
                  </div>
                </div>
              ) : (
                <span className="pl-vendor-value">{wholesaleVal ?? "–"}</span>
              )}
            </div>
          </div>
        );
      },
    },
    // {
    //   key: "status",
    //   label: "Status",
    //   width: 90,
    //   render: (value) => (
    //     <span className={`pl-status-badge ${value === "5" ? "pl-status-completed" : "pl-status-pending"}`}>
    //       {value === "5" ? "Completed" : "Pending"}
    //     </span>
    //   ),
    // },
    // {
    //   key: "verified_seller",
    //   label: "Verified",
    //   width: 90,
    //   render: (value) => (
    //     <span className={`pl-verified-badge ${value ? "pl-verified-yes" : "pl-verified-no"}`}>
    //       {value ? "Verified" : "Not Verified"}
    //     </span>
    //   ),
    // },
    {
      key: "subscribers",
      label: "Subs",
      width: 60,
      render: (value) => <span className="pl-subs">{value || 0}</span>,
    },
    {
      key: "createdAt",
      label: "Created",
      width: 100,
      render: (value) => <span className="pl-date">{formatDate(value)}</span>,
    },
    {
      key: "actions",
      label: "Actions",
      width: 130,
      render: (_, row) => {
        const product = row.raw || {};
        const isDeleted = product.is_delete;
        return (
          <div className="pl-actions-cell">
            {canEdit("products") && (
              <button className="pl-action-btn pl-action-edit" onClick={(e) => { e.stopPropagation(); handleEdit(product._id); }} title="Edit">
                <MdModeEdit />
              </button>
            )}
            {canDelete("products") && (
              <button className="pl-action-btn pl-action-delete" onClick={(e) => { e.stopPropagation(); isDeleted ? handleRevert(product._id, false) : handleDelete(product._id); }} title={isDeleted ? "Restore" : "Delete"}>
                {isDeleted ? <GrRevert /> : <MdDelete />}
              </button>
            )}
            <button className="pl-action-btn pl-action-desc" onClick={(e) => { e.stopPropagation(); handleDescription(product._id); }} title="Details">
              <MdDescription />
            </button>
            {canEdit("products") && (
              <button className="pl-action-btn pl-action-toggle" onClick={(e) => { e.stopPropagation(); handleStatus(product._id, product.status === "0" ? "5" : "0"); }} title="Toggle Status">
                {product.status === "0" ? <IoMdEyeOff /> : <IoMdEye />}
              </button>
            )}
          </div>
        );
      },
    },
  ], [canEdit, canDelete]);

  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, pages.totalItems);

  const toolbarLeftActions = (
    <div className="pl-toolbar-left">
      <div className="pl-search-box">
        <FaSearch className="pl-search-icon" />
        <input type="text" className="pl-search-input" placeholder="Product name..." name="name" value={products.name} onChange={handleOnChange} onKeyUp={(e) => e.key === "Enter" && searchProducts()} />
      </div>
      <input type="text" className="pl-filter-input" placeholder="SKU..." name="sku" value={products.sku} onChange={handleOnChange} onKeyUp={(e) => e.key === "Enter" && searchProducts()} />
      <select className="pl-filter-select" value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)}>
        {suppliers.map((sup) => <option key={sup._id} value={sup._id}>{sup.name}</option>)}
      </select>
      <select className="pl-filter-select pl-filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
        <option value="">All Status</option>
        <option value="5">Completed</option>
        <option value="0">Pending</option>
      </select>
      <select className="pl-filter-select" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
        {categories.map((cat, i) => <option key={i} value={cat.value}>{cat.name}</option>)}
      </select>
      <button className="pl-search-btn" onClick={searchProducts}><FaSearch /> Search</button>
    </div>
  );

  const toolbarRightActions = (
    <div className="pl-toolbar-right">
      <button className="pl-refresh-btn" onClick={fetchProductsList} title="Refresh">
        <FaSync className={loading ? "pl-spin" : ""} />
      </button>
      <button className="pl-toolbar-btn" onClick={handleExportCsv} title="Export"><FaDownload /> Export</button>
      <button className="pl-toolbar-btn" onClick={handleVerifySeller} title="Verify Seller"><FaCheckCircle /> Verify</button>
      <div className="pl-upload-group">
        <input type="file" id="pl-file-upload" className="pl-file-input" onChange={handleFile} />
        <label htmlFor="pl-file-upload" className="pl-upload-label"><FaUpload /></label>
        {uploadFile && <span className="pl-file-name">{uploadFile.name.substring(0, 10)}...</span>}
        <button className="pl-toolbar-btn pl-btn-primary" onClick={handleUploadProduct}>Upload</button>
      </div>
      <button className="pl-toolbar-btn pl-btn-primary" onClick={() => navigate("/admin/products/add")}><FaPlus /> New</button>
      <div className="pl-rows-picker">
        <span>Rows:</span>
        <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
          {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </div>
    </div>
  );

  return (
    <div className="pl-page">
      <div className="pl-table-section">
        <ExcelTable
          isLoading={initialLoading || loading}
          loadingText="Loading products..."
          data={tableRows}
          columns={columns}
          showToolbarExport={false}
          showToolbarFilter={false}
          showToolbarColumns={true}
          showToolbarFullscreen={true}
          toolbarLeftActions={toolbarLeftActions}
          toolbarRightActions={toolbarRightActions}
          onSelectionChange={(rows) => setSelectedProducts(rows)}
          selectedData={selectedProducts}
          selectable={true}
          sortable={true}
          resizable={true}
          showToolbar={true}
          title="Products"
          userId={currentUserId}
        />

        {productData.length > 0 && pages.totalPages > 1 && (
          <div className="pl-pagination">
            <div className="pl-pagination-info">Showing {startItem} to {endItem} of {pages.totalItems} products</div>
            <div className="pl-pagination-controls">
              <button className="pl-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>‹</button>
              {Array.from({ length: Math.min(5, pages.totalPages) }, (_, i) => {
                let pageNum;
                if (pages.totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= pages.totalPages - 2) pageNum = pages.totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <button key={pageNum} className={`pl-page-btn ${page === pageNum ? "pl-page-btn-active" : ""}`} onClick={() => setPage(pageNum)}>
                    {pageNum}
                  </button>
                );
              })}
              {pages.totalPages > 5 && page < pages.totalPages - 2 && (
                <>
                  <span className="pl-page-dots">...</span>
                  <button className="pl-page-btn" onClick={() => setPage(pages.totalPages)}>{pages.totalPages}</button>
                </>
              )}
              <button className="pl-page-btn" disabled={page >= pages.totalPages} onClick={() => setPage(page + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      <DeletePopUp heading="Delete Product" text="Are you sure you want to delete?" open={open} handleClose={handleClose} submitText="Yes" cancelText="No" handleSubmit={handleSubmit} handleCancel={handleClose} />
      
      {/* Export Processing Modal */}
      {exportProcessing && (
        <div className="export-processing-modal-backdrop">
          <div className="export-processing-modal">
            <div className="export-processing-modal-content">
              <div className="export-processing-spinner">
                <FaSync className={loading ? "pl-spin" : ""} style={{ fontSize: '24px', color: '#2563eb' }} />
              </div>
              <div className="export-processing-text">
                <h3>Exporting Data</h3>
                <p>{exportStatus || "Processing..."}</p>
                <div className="export-progress-bar-container">
                  <div className="export-progress-bar">
                    <div 
                      className="export-progress-bar-fill" 
                      style={{ width: `${exportProgress}%` }}
                    ></div>
                  </div>
                  <div className="export-progress-percentage">{exportProgress}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
